import { matchPath, useLocation } from "react-router";

import { navigation } from "../config/navigation";

export type ActiveMenuContext = Readonly<{
    selectedMenuKey?: string;
    openSubMenuKey?: string;
}>;

type MenuRouteMapping = Readonly<{
    routePath: string;
    selectedMenuKey: string;
    openSubMenuKey?: string;
}>;

const menuRouteMappings: ReadonlyArray<MenuRouteMapping> = navigation
    .flatMap((group) => {
        const subMenuMappings =
            group.subMenus?.flatMap((subMenu) =>
                subMenu.menuItems.map((menuItem) => ({
                    routePath: menuItem.route.path,
                    selectedMenuKey: menuItem.to,
                    openSubMenuKey: subMenu.key,
                })),
            ) ?? [];

        const unclassifiedMappings =
            group.unclassifiedItems?.map((menuItem) => ({
                routePath: menuItem.route.path,
                selectedMenuKey: menuItem.to,
            })) ?? [];

        return [...subMenuMappings, ...unclassifiedMappings];
    })
    // Prefer the most specific route path first.
    .sort((a, b) => b.routePath.length - a.routePath.length);

export const getActiveMenuContext = (pathname: string): ActiveMenuContext => {
    const activeMapping = menuRouteMappings.find((mapping) =>
        Boolean(
            matchPath(
                {
                    path: mapping.routePath,
                    end: false,
                },
                pathname,
            ),
        ),
    );

    if (!activeMapping) {
        return {};
    }

    return {
        selectedMenuKey: activeMapping.selectedMenuKey,
        openSubMenuKey: activeMapping.openSubMenuKey,
    };
};

export const useActiveMenuContext = (): ActiveMenuContext => {
    const { pathname } = useLocation();
    return getActiveMenuContext(pathname);
};
